import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface User {
  id: number
  email: string
  name: string
  role: 'customer' | 'staff' | 'admin' | 'callcenter_admin'
  phone?: string
  is_active: boolean
  created_at: string
  updated_at?: string
  must_change_password?: boolean
}

export interface AuthTokens {
  access_token: string
  refresh_token: string
  token_type: string
}

interface AuthState {
  // State
  user: User | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  setAuth: (user: User, tokens: AuthTokens) => void
  setUser: (user: User) => void
  setTokens: (tokens: AuthTokens) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  logout: () => void
  clearError: () => void

  // Computed
  isAdmin: () => boolean
  isStaff: () => boolean
  isCustomer: () => boolean
  hasRole: (role: string | string[]) => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial State
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      setAuth: (user: User, tokens: AuthTokens) => {
        set({
          user,
          tokens,
          isAuthenticated: true,
          error: null,
        })
      },

      setUser: (user: User) => {
        set({ user })
      },

      setTokens: (tokens: AuthTokens) => {
        set({ tokens })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      setError: (error: string | null) => {
        set({ error })
      },

      logout: () => {
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          error: null,
        })
      },

      clearError: () => {
        set({ error: null })
      },

      // Computed Properties
      isAdmin: () => {
        const { user } = get()
        return user?.role === 'admin'
      },

      isStaff: () => {
        const { user } = get()
        return user?.role === 'staff' || user?.role === 'admin' || user?.role === 'callcenter_admin'
      },

      isCustomer: () => {
        const { user } = get()
        return user?.role === 'customer'
      },

      hasRole: (role: string | string[]) => {
        const { user } = get()
        if (!user) return false
        
        if (Array.isArray(role)) {
          return role.includes(user.role)
        }
        
        return user.role === role
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
        error: state.error, // Persist error state too
      }),
    }
  )
) 